import React from 'react';
import { Box, SxProps } from '@mui/material';
import { Checkbox as HiveCheckbox, CheckboxProps as HiveCheckboxProps, Theme } from '@routific/hive';

interface Props extends Omit<HiveCheckboxProps, 'onChange' | 'inputProps'> {
  onChange?: (checked: boolean) => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & Record<string, any>;
  /**
   * Whether the checkbox should sit flush with the left edge of its container.
   * Offsets MUI's intrinsic padding so the icon aligns with surrounding content.
   * Set to false when the parent already provides left spacing (e.g. table cells). Defaults to true.
   */
  flushLeft?: boolean;
}

const FLUSH_OFFSET = `-${Theme.Spacing.smSpacing}`;

const Checkbox: React.FC<Props> = ({
  onChange, inputProps, label, description, sx, flushLeft = true, ...props
}) => {
  const handleChange = (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onChange?.(checked);
  };

  // When `label` is set, Hive wraps the checkbox in a FormControlLabel whose sx it owns.
  // Wrap so we can offset that wrapper from the outside; user-provided sx is forwarded here
  // so callers can still add margins/spacing around the full control.
  if (label !== undefined && label !== null) {
    // With a `description`, Hive renders a CSS grid (no FormControlLabel), so the flush
    // offset has to nudge the whole control; without one it targets the FormControlLabel.
    const flushSx = description != null
      ? { marginLeft: FLUSH_OFFSET }
      : { '& .MuiFormControlLabel-root': { marginLeft: FLUSH_OFFSET, marginRight: 0 } };
    const wrapperSx: SxProps = {
      ...(flushLeft && flushSx),
      ...sx,
    };

    return (
      <Box sx={wrapperSx}>
        <HiveCheckbox
          {...props}
          label={label}
          description={description}
          onChange={handleChange}
          inputProps={inputProps}
        />
      </Box>
    );
  }

  return (
    <HiveCheckbox
      {...props}
      onChange={handleChange}
      inputProps={inputProps}
      sx={{
        ...(flushLeft && { marginLeft: FLUSH_OFFSET }),
        ...sx,
      }}
    />
  );
};

export default Checkbox;
